import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toUtcIso, Localization, usePostMessageContext } from 'connex-cds';
import { isObject } from 'lodash';
import { DateTime } from 'luxon';
import React, { useContext } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Toast from './notification/Toast/Toast';
import { mobileTicket } from '../../../../api';
import { usePostMessageListener } from './datastore/usePostMessageListener';
import { useCompanySetup } from './MasterDataProvider';
import { WaitingForTicket } from './WaitingForTicket';
import { useGetPaymentMethod } from '../../../../query-hooks/payment-method';
import { useGetOrder } from '../../../../query-hooks/order';
import { findNextPendingDriverTicket } from '../../../../query-hooks/tickets';
import { Logger } from '../../../../util/log/logger';

const TicketContext = React.createContext();

export const useTicketContext = () => {
  const context = useContext(TicketContext);
  if (!context) {
    throw new Error(`useTicketContext cannot be rendered outside of the TicketContext context provider`);
  }
  return context;
};

export const useTicket = (ticketRef, enabled, queryParams = {}) => {
  const { entityRef } = useParams();
  const companySetup = useCompanySetup();

  const query = useQuery({
    queryKey: ['ticket', entityRef, ticketRef],
    queryFn: () => mobileTicket.getTicket({ entityRef, ticketRef, queryParams }),
    refetchInterval: (_data, query) => {
      if (query.state.dataUpdateCount < 2) return 1;
      return companySetup?.data?.pollingInterval ? companySetup.data.pollingInterval * 1000 : 60000;
    },
    staleTime: Infinity,
    enabled: enabled && !!ticketRef,
  });

  if (!ticketRef) {
    return {
      ...query,
      data: undefined,
      isLoading: false,
      isError: false,
    };
  }

  return query;
};

const ACCEPTED_REJECTED = ['ACCEPTED', 'REJECTED'];

/** TrackIt / host postMessage may stringify booleans; treat only explicit inactive as false. */
const messageTicketIsInactive = active => active === false || active === 'false' || active === 'False';

const messageTicketIsActive = active => active === true || active === 'true' || active === 'True';

const normalizeTicketRef = ref => (typeof ref === 'string' ? ref.trim().toLowerCase() : ref);

export const TicketContextProvider = ({
  children,
  driverExperience = false,
  selectedTicket = false,
  activeTicket = false,
}) => {
  const { entityRef, truckNumber } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const ticketCrnRef = React.useRef();
  const voidedToastShownRef = React.useRef(false);
  const translateMessage = Localization.useTranslateMessage();

  const [fetchedDateTime, setFetchedDateTime] = React.useState(toUtcIso(DateTime.now()));
  const [currentTicketRef, setCurrentTicketRef] = React.useState(() =>
    activeTicket && location?.state?.crn ? location.state.crn : undefined
  );
  const [queryEnabled, setQueryEnabled] = React.useState(false);
  const [finalized, setFinalized] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);
  const [refreshing, setRefreshing] = React.useState(false);

  const companySetup = useCompanySetup();

  const ticketQuery = useTicket(currentTicketRef, queryEnabled, { expand: 'signatures' });

  const orderData = useGetOrder(ticketQuery?.data?.orderRef, companySetup);

  const paymentMethodData = useGetPaymentMethod(orderData?.data?.paymentMethod?.paymentMethodRef, companySetup);

  const { sendMessage } = usePostMessageContext();

  const isStandAloneTicket = React.useMemo(() => selectedTicket || activeTicket, [selectedTicket, activeTicket]);

  const isRefetching = React.useMemo(
    () => ticketQuery?.isLoading || ticketQuery?.isFetching || refreshing,
    [ticketQuery?.isLoading, ticketQuery?.isFetching, refreshing]
  );

  React.useEffect(() => {
    ticketCrnRef.current = ticketQuery?.data?.crn;
  }, [ticketQuery?.data?.crn]);

  React.useEffect(() => {
    setQueryEnabled(!!currentTicketRef);
  }, [currentTicketRef]);

  React.useEffect(() => {
    if (ticketQuery?.data && !ticketQuery?.data?.isVoided) {
      voidedToastShownRef.current = false;
    }
  }, [ticketQuery?.data]);

  React.useEffect(() => {
    if (
      ACCEPTED_REJECTED.includes(ticketQuery?.data?.sk?.customerStatus) &&
      (ACCEPTED_REJECTED.includes(ticketQuery?.data?.customerStatus) ||
        ACCEPTED_REJECTED.includes(ticketQuery?.data?.customerStatus?.statusCode))
    ) {
      setFinalized(true);
      setSubmitted(true);
    }
  }, [ticketQuery?.data?.customerStatus, ticketQuery?.data?.sk?.customerStatus]);

  const reset = React.useCallback(
    (clearTicket = true, callback) => {
      setQueryEnabled(false);
      queryClient.invalidateQueries({ queryKey: ['ticket', entityRef, currentTicketRef] });

      if (clearTicket && callback) {
        setFinalized(false);
        setSubmitted(false);

        callback();
      }

      if (clearTicket && !callback) {
        setFinalized(false);
        setSubmitted(false);
        setCurrentTicketRef(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentTicketRef, driverExperience, entityRef, navigate, queryClient, truckNumber]
  );

  const { refetch } = ticketQuery;

  const refreshTicket = React.useCallback(async () => {
    setRefreshing(true);

    try {
      const result = await refetch();
      const updatedTicket = result?.data;

      if (updatedTicket?.isVoided) {
        reset();

        return;
      }

      if (companySetup?.data?.isRealTimeUpdatesEnabled === true && updatedTicket?.crn) {
        await queryClient.invalidateQueries({
          queryKey: ['setup', entityRef, 'coc-setup', updatedTicket.crn],
        });
      }

      if (updatedTicket?.crn) {
        await queryClient.invalidateQueries({
          queryKey: ['ticket-attachments-driver-viewable', entityRef, updatedTicket?.crn],
        });
      }

      setFetchedDateTime(toUtcIso(DateTime.now()));
    } catch (error) {
      console.error('Failed to refresh ticket:', error);
    } finally {
      setRefreshing(false);
    }
  }, [companySetup?.data?.isRealTimeUpdatesEnabled, entityRef, queryClient, refetch, reset]);

  const handleVoidedDismiss = React.useCallback(async () => {
    const voidedCrn = currentTicketRef;

    if (activeTicket) {
      try {
        const nextTicket = await findNextPendingDriverTicket(entityRef, voidedCrn);
        if (nextTicket) {
          queryClient.invalidateQueries({ queryKey: ['ticket', entityRef, voidedCrn] });
          setCurrentTicketRef(nextTicket.crn);
          return;
        }
      } catch {
        /* fall through */
      }
      navigate(`/${entityRef}/active-ticket`, { state: { excludeCrn: voidedCrn }, replace: true });
    } else if (selectedTicket) {
      navigate(`/${entityRef}/ticket-list`, { replace: true });
    } else {
      reset();
    }
  }, [activeTicket, selectedTicket, currentTicketRef, reset, navigate, entityRef, queryClient]);

  React.useEffect(() => {
    if (!ticketQuery?.data?.isVoided || voidedToastShownRef.current) return;

    voidedToastShownRef.current = true;
    setQueryEnabled(false);

    toast(<Toast type="ERROR" message={translateMessage('ticketVoidedNotification')} />, {
      autoClose: false,
      // closeOnClick: false,
      onClose: handleVoidedDismiss,
    });
  }, [ticketQuery?.data?.isVoided, translateMessage, handleVoidedDismiss]);

  const listener = React.useCallback(
    event => {
      // alert(JSON.stringify(event?.data || {}));

      let message;

      // TODO: move this into the usePostMessageListener hook.
      if (isObject(event?.data)) {
        message = event?.data;
      } else {
        try {
          message = JSON.parse(event?.data);
        } catch (e) {
          message = event?.data;
        }
      }

      if (message?.type === 'status-event') {
        sendMessage(JSON.stringify({ type: 'debug', ack: message }));
        Logger.debug(
          `Mobile Ticket - TicketContext.js status-event ack ${JSON.stringify({
            currentTicket: currentTicketRef,
          })}`
        );
        const newTicket = {
          ...ticketQuery?.data,
          ticketEvents: {
            ...ticketQuery?.data?.ticketEvents,
            [message.name]: {
              eventDateTime: message.timestamp,
            },
          },
        };
        queryClient.setQueryData(['ticket', entityRef, newTicket?.crn], newTicket);
      }

      if (message?.type === 'ticket') {
        sendMessage(JSON.stringify({ type: 'debug', ack: message }));
        Logger.debug(
          `Mobile Ticket - TicketContext.js ticket ack ${JSON.stringify({
            currentTicket: currentTicketRef,
            active: message?.active,
            keepTicketOpenAfterSubmission: companySetup?.data?.keepTicketOpenAfterSubmission,
          })}`
        );

        // Host inactive: cancel/divert/reassign — always drop the current ticket and wizard.
        // Do not use keepTicketOpenAfterSubmission here; that flag is for post-submit UX only.
        if (messageTicketIsInactive(message?.active)) {
          reset(true);
        }

        if (messageTicketIsActive(message?.active)) {
          const incomingRef = message?.crn ?? message?.ticketRef;
          if (!incomingRef) {
            return;
          }
          // Ignore new ticket if it's the current one (case-insensitive UUID compare).
          if (normalizeTicketRef(currentTicketRef) === normalizeTicketRef(incomingRef)) {
            return;
          }

          reset(true, () => {
            // we need to wait a beat to allow the navigation in the reset function to occur.
            const ticketRef = incomingRef;
            setCurrentTicketRef(ticketRef);

            if (driverExperience) {
              navigate(`driver/${entityRef}/${truckNumber}/ticketDetail`);
            } else {
              navigate(`/${entityRef}/${truckNumber}/mt/ticketDetail`);
            }
          });
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      sendMessage,
      ticketQuery?.data,
      ticketQuery?.vehicleId,
      queryClient,
      entityRef,
      reset,
      currentTicketRef,
      companySetup?.data?.keepTicketOpenAfterSubmission,
      driverExperience,
      navigate,
      truckNumber,
    ]
  );

  usePostMessageListener(listener);

  if (!ticketQuery?.data) {
    if (activeTicket && !ticketQuery?.isLoading && !ticketQuery?.isFetching && !currentTicketRef) {
      return <Navigate to=".." replace />;
    }
    return <WaitingForTicket busy={ticketQuery?.isLoading} setTicketRef={setCurrentTicketRef} />;
  }

  return (
    <TicketContext.Provider
      value={{
        ticket: ticketQuery?.data,
        order: orderData?.data,
        refreshTicket,
        refreshing: isRefetching,
        loading: ticketQuery?.isLoading,
        fetchedDateTime,
        reset,
        finalized,
        setFinalized,
        submitted,
        setSubmitted,
        paymentMethod: paymentMethodData?.data,
        selectedTicket,
        activeTicket,
        isStandAloneTicket,
      }}
    >
      {children}
    </TicketContext.Provider>
  );
};
